/**
 * Formats a date/time string to show only the time
 * @param dateTimeString - Date string in format "YYYY-MM-DD HH:mm" (GMT)
 * @param language - Current language code ('en', 'fr', 'ar')
 * @returns Formatted string like "7:00 PM" or "19:00" (24-hour for French/Arabic)
 */
export function formatMatchTime(dateTimeString: string, language: string = 'en'): string {
  try {
    // Parse the time part
    const [, timePart] = dateTimeString.split(' ');
    const [hours, minutes] = timePart.split(':').map(Number);
    
    // Use 24-hour format for French and Arabic, 12-hour for English
    if (language === 'fr' || language === 'ar') {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    } else {
      // English: 12-hour format with AM/PM
    const hours12 = hours % 12 || 12;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
    }
  } catch {
    // Return original if parsing fails
    return dateTimeString;
  }
}

/**
 * Gets the date key for grouping matches
 * @param dateTimeString - Date string in format "YYYY-MM-DD HH:mm"
 * @returns Date string in format "YYYY-MM-DD" for grouping
 */
export function getDateKey(dateTimeString: string): string {
  try {
    const [datePart] = dateTimeString.split(' ');
    return datePart;
  } catch {
    return dateTimeString;
  }
}

/**
 * Formats a date key for display as a section header
 * @param dateKey - Date string in format "YYYY-MM-DD"
 * @param t - Translation function from useLanguage hook
 * @returns Formatted string like "Today", "Tomorrow", "Dec 21, 2025"
 */
export function formatDateHeader(dateKey: string, t: (key: string) => string): string {
  try {
    const [year, month, day] = dateKey.split('-').map(Number);
    const matchDay = new Date(Date.UTC(year, month - 1, day));
    const now = new Date();
    
    // Get today's date at midnight UTC
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    
    // Calculate difference in days
    const diffDays = Math.floor((matchDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return t('time.today');
    } else if (diffDays === 1) {
      return t('time.tomorrow');
    } else if (diffDays === -1) {
      return t('time.yesterday');
    } else if (diffDays > 1 && diffDays <= 7) {
      // Within next week - show day name
      const dayKeys = ['time.sunday', 'time.monday', 'time.tuesday', 'time.wednesday', 'time.thursday', 'time.friday', 'time.saturday'];
      return t(dayKeys[matchDay.getUTCDay()]);
    } else {
      // Further out - show full date
      const monthKeys = ['time.january', 'time.february', 'time.march', 'time.april', 'time.may', 'time.june', 
                         'time.july', 'time.august', 'time.september', 'time.october', 'time.november', 'time.december'];
      return `${t(monthKeys[month - 1])} ${day}, ${year}`;
    }
  } catch {
    return dateKey;
  }
}

/**
 * Formats a timestamp as "time ago" (e.g., "2 min ago", "1 hour ago")
 * @param timestamp - ISO timestamp string
 * @param t - Translation function from useLanguage hook
 * @returns Formatted string like "just now", "2 min ago", "1 hour ago"
 */
export function formatTimeAgo(timestamp: string, t: (key: string) => string): string {
  try {
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now.getTime() - past.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 10) {
      return t('time.justNow');
    } else if (diffSeconds < 60) {
      return t('time.fewSecondsAgo');
    } else if (diffMinutes < 60) {
      const minuteText = diffMinutes === 1 ? t('time.minute') : t('time.minutes');
      return `${diffMinutes} ${minuteText} ${t('time.ago')}`;
    } else if (diffHours < 24) {
      const hourText = diffHours === 1 ? t('time.hour') : t('time.hours');
      return `${diffHours} ${hourText} ${t('time.ago')}`;
    } else if (diffDays < 7) {
      const dayText = diffDays === 1 ? t('time.day') : t('time.days');
      return `${diffDays} ${dayText} ${t('time.ago')}`;
    } else {
      // More than a week - show date using translated month abbreviations
      const monthKeys = ['time.jan', 'time.feb', 'time.mar', 'time.apr', 'time.may', 'time.jun', 
                         'time.jul', 'time.aug', 'time.sep', 'time.oct', 'time.nov', 'time.dec'];
      return `${t(monthKeys[past.getMonth()])} ${past.getDate()}`;
    }
  } catch {
    return '';
  }
}

